import { Box, Link, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { GiphyFetch } from "@giphy/js-fetch-api";
import { Grid } from "@giphy/react-components";
import type { IGif } from "@giphy/js-types";
import type { GifsResult } from "@giphy/js-fetch-api";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type SyntheticEvent } from "react";
import { getGiphyApiKey } from "../config/giphyEnv";

const EMPTY_GIFS: Promise<GifsResult> = Promise.resolve({
  data: [],
  pagination: { count: 0, total_count: 0, offset: 0 },
  meta: { status: 200, msg: "OK", response_id: "" },
});

/** GIPHY `type` for search/trending — see https://developers.giphy.com/docs/api/endpoint/#search */
type GiphyCatalog = "gifs" | "stickers";

type Props = {
  onPick: (giphyId: string) => void;
  /** Rate-limited: pause picking only */
  pickDisabled?: boolean;
  /** Fixed width for narrow sidebars; omit to size to parent container */
  gridWidth?: number;
  /**
   * Phone / tablet strip: one visual row of picks (short height, fewer per fetch).
   * Desktop sidebar can omit for a taller scroll area.
   */
  compactStrip?: boolean;
};

/**
 * Inline GIPHY search + grid (no modal). Search field stays editable; grid fills when `REACT_APP_GIPHY_API_KEY` is set at build time.
 */
export function TableGiphyInlinePanel({
  onPick,
  pickDisabled,
  gridWidth: gridWidthProp,
  compactStrip = false,
}: Props) {
  const apiKey = getGiphyApiKey();
  const gf = useMemo(() => (apiKey ? new GiphyFetch(apiKey) : null), [apiKey]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [catalog, setCatalog] = useState<GiphyCatalog>("gifs");
  const wrapRef = useRef<HTMLDivElement>(null);
  const [measuredW, setMeasuredW] = useState(280);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(t);
  }, [search]);

  useLayoutEffect(() => {
    if (gridWidthProp != null) return;
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      setMeasuredW(Math.max(140, Math.floor(el.getBoundingClientRect().width)));
    });
    ro.observe(el);
    setMeasuredW(Math.max(140, Math.floor(el.getBoundingClientRect().width)));
    return () => ro.disconnect();
  }, [gridWidthProp]);

  const gridWidth = gridWidthProp ?? measuredW;
  const gridColumns = compactStrip ? (gridWidth < 240 ? 3 : 4) : gridWidth < 200 ? 2 : 3;
  const gridMaxHeight = compactStrip ? 90 : 200;
  /** One screen row on phones: keep limit ≤ columns so the first page is a single row. */
  const pageLimit = compactStrip ? gridColumns : 10;

  const fetchGifs = useCallback(
    (offset: number) => {
      if (!gf) return EMPTY_GIFS;
      const opts = { offset, limit: pageLimit, rating: "pg" as const, type: catalog };
      if (debouncedSearch) {
        return gf.search(debouncedSearch, opts);
      }
      return gf.trending(opts);
    },
    [gf, debouncedSearch, catalog, pageLimit]
  );

  const onGifClick = useCallback(
    (gif: IGif, e: SyntheticEvent<HTMLElement>) => {
      e.preventDefault();
      if (pickDisabled) return;
      onPick(String(gif.id));
    },
    [onPick, pickDisabled]
  );

  return (
    <Box ref={wrapRef} sx={{ width: "100%", minWidth: 0 }}>
      {apiKey ? (
        <ToggleButtonGroup
          exclusive
          size="small"
          value={catalog}
          onChange={(_, v: GiphyCatalog | null) => v && setCatalog(v)}
          sx={{ mb: 0.75, display: "flex", "& .MuiToggleButton-root": { flex: 1, py: 0.35, fontSize: "0.72rem" } }}
        >
          <ToggleButton value="gifs">GIFs</ToggleButton>
          <ToggleButton value="stickers">Stickers</ToggleButton>
        </ToggleButtonGroup>
      ) : null}
      <TextField
        fullWidth
        size="small"
        placeholder={catalog === "stickers" ? "Search stickers…" : "Search GIFs…"}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        autoComplete="off"
        sx={{
          mb: 0.75,
          "& .MuiOutlinedInput-root": { backgroundColor: "rgba(255,255,255,0.06)" },
          "& .MuiInputBase-input": { fontSize: "0.82rem", py: 0.75 },
        }}
      />
      {!apiKey ? (
        <Typography variant="caption" sx={{ display: "block", color: "rgba(255,255,255,0.5)", mb: 0.5, lineHeight: 1.4 }}>
          The app still does not see <code style={{ fontSize: "0.68rem" }}>REACT_APP_GIPHY_API_KEY</code> in the bundle.
          {process.env.NODE_ENV === "development" ? (
            <>
              {" "}
              Stop <code style={{ fontSize: "0.68rem" }}>npm start</code>, run{" "}
              <code style={{ fontSize: "0.68rem" }}>rm -rf node_modules/.cache</code>, start again, then hard-refresh the
              browser.
            </>
          ) : (
            <> Rebuild the frontend with this variable set in <code style={{ fontSize: "0.68rem" }}>.env.production</code>.</>
          )}
        </Typography>
      ) : null}
      {apiKey ? (
        <Box
          sx={{
            maxHeight: gridMaxHeight,
            overflowY: compactStrip ? "hidden" : "auto",
            overflowX: compactStrip ? "auto" : "hidden",
            borderRadius: 1,
            bgcolor: "rgba(0,0,0,0.25)",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <Grid
            key={`${catalog}-${debouncedSearch || "__trending__"}-${compactStrip ? "c" : "f"}`}
            width={gridWidth}
            columns={gridColumns}
            gutter={compactStrip ? 3 : 4}
            fetchGifs={fetchGifs}
            onGifClick={onGifClick}
            hideAttribution
          />
        </Box>
      ) : null}
      <Typography variant="caption" component="div" sx={{ mt: 0.35, textAlign: "center", opacity: 0.45 }}>
        <Link href="https://giphy.com" target="_blank" rel="noreferrer" sx={{ color: "inherit" }}>
          GIPHY
        </Link>
      </Typography>
    </Box>
  );
}
